import { useState, useRef, useEffect } from "react";
import { Loader2, Rocket, Sparkles, CheckCircle2, CreditCard, Fuel } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

interface PremiumSubmitButtonProps {
  onClick: () => void;
  isSubmitting: boolean;
  isDisabled: boolean;
  total: number;
  isFormComplete?: boolean;
}

type SubmitStage = 'idle' | 'validating' | 'processing' | 'redirecting';

export const PremiumSubmitButton = ({ 
  onClick, 
  isSubmitting, 
  isDisabled, 
  total,
  isFormComplete 
}: PremiumSubmitButtonProps) => {
  const [ripples, setRipples] = useState<{ x: number; y: number; id: number }[]>([]);
  const [stage, setStage] = useState<SubmitStage>('idle');
  const [showConfetti, setShowConfetti] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { formatPrice } = useLanguage();

  // Multi-stage loading animation
  useEffect(() => {
    if (isSubmitting) {
      setStage('validating');
      const timer1 = setTimeout(() => setStage('processing'), 800);
      const timer2 = setTimeout(() => setStage('redirecting'), 2000);
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    } else {
      setStage('idle');
    }
  }, [isSubmitting]);

  // Confetti on form complete
  useEffect(() => {
    if (isFormComplete && !isDisabled) {
      setShowConfetti(true);
      const timer = setTimeout(() => setShowConfetti(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [isFormComplete, isDisabled]);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled || isSubmitting) return;

    // Create ripple effect
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect) {
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const id = Date.now();
      setRipples(prev => [...prev, { x, y, id }]);
      setTimeout(() => {
        setRipples(prev => prev.filter(r => r.id !== id));
      }, 600);
    }

    onClick();
  };

  const getStageContent = () => {
    switch (stage) {
      case 'validating':
        return (
          <>
            <CheckCircle2 className="w-5 h-5 animate-pulse" />
            <span>Validating...</span>
          </>
        );
      case 'processing':
        return (
          <>
            <CreditCard className="w-5 h-5 animate-bounce" />
            <span>Preparing checkout...</span>
          </>
        );
      case 'redirecting':
        return (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Redirecting to payment...</span>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="relative">
      {/* Confetti particles */}
      {showConfetti && (
        <div className="absolute inset-0 pointer-events-none overflow-visible">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className={cn(
                "absolute w-2 h-2 rounded-full",
                i % 3 === 0 ? "bg-neon-cyan" : i % 3 === 1 ? "bg-neon-pink" : "bg-neon-purple"
              )}
              style={{
                left: `${Math.random() * 100}%`,
                top: '50%',
                animation: `confetti-fall ${1 + Math.random()}s ease-out forwards`,
                animationDelay: `${Math.random() * 0.3}s`
              }}
            />
          ))}
        </div>
      )}

      <button
        ref={buttonRef}
        onClick={handleClick}
        disabled={isDisabled || isSubmitting}
        className={cn(
          "relative w-full h-14 md:h-16 rounded-xl font-bold text-base md:text-lg overflow-hidden",
          "transition-all duration-300 will-change-transform",
          "bg-gradient-to-r from-orange-500 via-neon-pink to-orange-600",
          "shadow-[0_0_30px_rgba(249,115,22,0.4)]",
          "hover:shadow-[0_0_50px_rgba(249,115,22,0.6)] hover:scale-[1.02]",
          "active:scale-[0.98] active:shadow-[0_0_20px_rgba(249,115,22,0.3)]",
          isDisabled && "opacity-50 cursor-not-allowed hover:scale-100 hover:shadow-none",
          isFormComplete && !isDisabled && !isSubmitting && "animate-ready-pulse",
          isSubmitting && "pointer-events-none"
        )}
      >
        {/* Ripple effects */}
        {ripples.map(ripple => (
          <span
            key={ripple.id}
            className="absolute w-4 h-4 rounded-full bg-white/40 animate-ripple pointer-events-none"
            style={{
              left: ripple.x - 8,
              top: ripple.y - 8,
            }}
          />
        ))}

        {/* Shimmer overlay when loading */}
        {isSubmitting && (
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer-fast" />
        )}

        {/* Progress bar during submission */}
        {isSubmitting && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-black/20">
            <div 
              className="h-full bg-white/50 transition-all duration-1000 ease-out"
              style={{ 
                width: stage === 'validating' ? '33%' : stage === 'processing' ? '66%' : '100%' 
              }}
            />
          </div>
        )}

        {/* Button content */}
        <span className="relative z-10 flex items-center justify-center gap-2 md:gap-3">
          {isSubmitting ? (
            getStageContent()
          ) : (
            <>
              {isFormComplete && <Sparkles className="w-4 h-4 md:w-5 md:h-5 animate-pulse" />}
              <Fuel className="w-4 h-4 md:w-5 md:h-5" />
              <span>Launch My Business</span>
              <span className="px-2 md:px-3 py-1 rounded-full bg-white/20 font-extrabold text-sm md:text-base">
                {formatPrice(total)}
              </span>
              <Rocket className="w-4 h-4 md:w-5 md:h-5 animate-bounce-x" />
            </>
          )}
        </span>

        {/* Glow ring when ready */}
        {isFormComplete && !isDisabled && !isSubmitting && (
          <div className="absolute inset-0 rounded-xl border-2 border-white/30 animate-glow-ring pointer-events-none" />
        )}
      </button>

      {/* Helper text */}
      {isDisabled && !isSubmitting && (
        <p className="text-center text-xs text-muted-foreground mt-2 animate-pulse">
          Complete all required fields to continue
        </p>
      )}
    </div>
  );
};
